import React, { useState, useEffect } from 'react';
import { depotsApi, zonesApi, locationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  Plus, Warehouse, MapPin, Layers, ChevronRight, 
  AlertTriangle, Settings, Eye, Trash2
} from 'lucide-react';

const CATEGORIES = ['F1', 'F2', 'F3', 'F4', 'T1', 'T2', 'P1', 'P2'];  // Catégories d'usage
const STORAGE_CLASSES = ['1.1G', '1.2G', '1.3G', '1.4G', '1.4S'];  // Classes ADR pour stockage/timbrage

const DepotsPage = () => {
  const [depots, setDepots] = useState([]);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showCreateDepotDialog, setShowCreateDepotDialog] = useState(false);
  const [showCreateZoneDialog, setShowCreateZoneDialog] = useState(false);
  const [showCreateLocationDialog, setShowCreateLocationDialog] = useState(false);
  const [showEditDepotDialog, setShowEditDepotDialog] = useState(false);
  const [editDepotForm, setEditDepotForm] = useState(null);

  const [depotForm, setDepotForm] = useState({
    name: '',
    code: '',
    address: '',
    latitude: '',
    longitude: '',
    alert_threshold_percent: 80,
    capacities: { '1.1G': '', '1.2G': '', '1.3G': '', '1.4G': '', '1.4S': '' }
  });

  const [zoneForm, setZoneForm] = useState({
    name: '',
    code: '',
    zone_type: 'storage',
  });

  const [locationForm, setLocationForm] = useState({
    code: '',
    alley: '',
    rack: '',
    level: '',
    position: '',
    max_nec: '',
  });

  useEffect(() => {
    fetchDepots();
  }, []);

  useEffect(() => {
    if (selectedDepot) {
      fetchZones(selectedDepot.id);
    }
  }, [selectedDepot]);

  useEffect(() => {
    if (selectedZone) {
      fetchLocations(selectedZone.id);
    }
  }, [selectedZone]);

  const fetchDepots = async () => {
    try {
      setLoading(true);
      const response = await depotsApi.getAll();
      setDepots(response.data);
      if (response.data.length > 0 && !selectedDepot) {
        setSelectedDepot(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching depots:', error);
      toast.error('Erreur lors du chargement des dépôts');
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (depotId) => {
    try {
      const response = await zonesApi.getAll({ depot_id: depotId });
      setZones(response.data);
      setSelectedZone(null);
      setLocations([]);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchLocations = async (zoneId) => {
    try {
      const response = await locationsApi.getAll({ zone_id: zoneId });
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleCreateDepot = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...depotForm,
        latitude: depotForm.latitude ? parseFloat(depotForm.latitude) : null,
        longitude: depotForm.longitude ? parseFloat(depotForm.longitude) : null,
        capacities: Object.fromEntries(
          Object.entries(depotForm.capacities)
            .filter(([_, v]) => v !== '')
            .map(([k, v]) => [k, parseFloat(v)])
        )
      };
      await depotsApi.create(data);
      toast.success('Depot cree avec succes');
      setShowCreateDepotDialog(false);
      fetchDepots();
      setDepotForm({
        name: '',
        code: '',
        address: '',
        latitude: '',
        longitude: '',
        alert_threshold_percent: 80,
        capacities: { '1.1G': '', '1.2G': '', '1.3G': '', '1.4G': '', '1.4S': '' }
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    try {
      await zonesApi.create({
        ...zoneForm,
        depot_id: selectedDepot.id
      });
      toast.success('Zone créée avec succès');
      setShowCreateZoneDialog(false);
      fetchZones(selectedDepot.id);
      setZoneForm({ name: '', code: '', zone_type: 'storage' });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...locationForm,
        zone_id: selectedZone.id,
        code: `${locationForm.alley}-${locationForm.rack}-${locationForm.level}-${locationForm.position}`,
        max_nec: locationForm.max_nec ? parseFloat(locationForm.max_nec) : null
      };
      await locationsApi.create(data);
      toast.success('Emplacement créé avec succès');
      setShowCreateLocationDialog(false);
      fetchLocations(selectedZone.id);
      setLocationForm({ code: '', alley: '', rack: '', level: '', position: '', max_nec: '' });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const getCapacityColor = (percent) => {
    if (percent >= 100) return 'bg-red-500';
    if (percent >= 80) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getCapacityPercent = (depot, category) => {
    const capacity = depot.capacities?.[category] || 0;
    const usage = depot.current_usage?.[category]?.nec || 0;
    return capacity > 0 ? Math.min((usage / capacity) * 100, 100) : 0;
  };

  const handleOpenEditDepot = () => {
    if (!selectedDepot) return;
    setEditDepotForm({
      name: selectedDepot.name || '',
      code: selectedDepot.code || '',
      address: selectedDepot.address || '',
      latitude: selectedDepot.latitude != null ? String(selectedDepot.latitude) : '',
      longitude: selectedDepot.longitude != null ? String(selectedDepot.longitude) : '',
      alert_threshold_percent: selectedDepot.alert_threshold_percent || 80,
      capacities: {
        '1.1G': selectedDepot.capacities?.['1.1G'] != null ? String(selectedDepot.capacities['1.1G']) : '',
        '1.2G': selectedDepot.capacities?.['1.2G'] != null ? String(selectedDepot.capacities['1.2G']) : '',
        '1.3G': selectedDepot.capacities?.['1.3G'] != null ? String(selectedDepot.capacities['1.3G']) : '',
        '1.4G': selectedDepot.capacities?.['1.4G'] != null ? String(selectedDepot.capacities['1.4G']) : '',
        '1.4S': selectedDepot.capacities?.['1.4S'] != null ? String(selectedDepot.capacities['1.4S']) : '',
      },
    });
    setShowEditDepotDialog(true);
  };

  const handleSaveEditDepot = async (e) => {
    e.preventDefault();
    if (!selectedDepot || !editDepotForm) return;
    try {
      const data = {
        name: editDepotForm.name,
        code: editDepotForm.code,
        address: editDepotForm.address,
        latitude: editDepotForm.latitude ? parseFloat(editDepotForm.latitude) : null,
        longitude: editDepotForm.longitude ? parseFloat(editDepotForm.longitude) : null,
        alert_threshold_percent: editDepotForm.alert_threshold_percent,
        capacities: Object.fromEntries(
          Object.entries(editDepotForm.capacities)
            .filter(([, v]) => v !== '')
            .map(([k, v]) => [k, parseFloat(v)])
        ),
      };
      await depotsApi.update(selectedDepot.id, data);
      toast.success('Dépôt mis à jour');
      setShowEditDepotDialog(false);
      fetchDepots();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'));
    }
  };

  const handleDeleteDepot = async () => {
    if (!selectedDepot) return;
    if (!window.confirm(`Supprimer le dépôt "${selectedDepot.name}" ? Cette action est irréversible.`)) return;
    try {
      await depotsApi.delete(selectedDepot.id);
      toast.success('Dépôt supprimé');
      setShowEditDepotDialog(false);
      setSelectedDepot(null);
      setSelectedZone(null);
      fetchDepots();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  return (
    <div className="space-y-6" data-testid="depots-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Dépôts & Emplacements</h1>
          <p className="text-zinc-400 text-sm mt-1">Gestion de la structure de stockage</p>
        </div>
        <Dialog open={showCreateDepotDialog} onOpenChange={setShowCreateDepotDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-depot-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Dépôt
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Nouveau Dépôt</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateDepot} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Nom du dépôt *</Label>
                  <Input
                    value={depotForm.name}
                    onChange={(e) => setDepotForm({...depotForm, name: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Dépôt Principal"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Code *</Label>
                  <Input
                    value={depotForm.code}
                    onChange={(e) => setDepotForm({...depotForm, code: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                    placeholder="DEP-01"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Adresse</Label>
                <Input
                  value={depotForm.address}
                  onChange={(e) => setDepotForm({...depotForm, address: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Adresse complete du depot"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Latitude</Label>
                  <Input type="number" step="0.000001"
                    value={depotForm.latitude}
                    onChange={(e) => setDepotForm({...depotForm, latitude: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                    placeholder="43.0896" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Longitude</Label>
                  <Input type="number" step="0.000001"
                    value={depotForm.longitude}
                    onChange={(e) => setDepotForm({...depotForm, longitude: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                    placeholder="0.5917" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Seuil d'alerte capacité (%)</Label>
                <Input
                  type="number"
                  value={depotForm.alert_threshold_percent}
                  onChange={(e) => setDepotForm({...depotForm, alert_threshold_percent: parseInt(e.target.value)})}
                  className="bg-zinc-950 border-zinc-800 text-white w-32"
                  min="50"
                  max="100"
                />
              </div>
              <div className="space-y-3">
                <Label className="text-zinc-300">Capacités maximales par classe de stockage (kg NEC)</Label>
                <p className="text-xs text-zinc-500">Classes ADR pour le timbrage du dépôt</p>
                <div className="grid grid-cols-5 gap-3">
                  {STORAGE_CLASSES.map(cls => (
                    <div key={cls} className="space-y-1">
                      <Label className="text-xs text-zinc-500">{cls}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={depotForm.capacities[cls] || ''}
                        onChange={(e) => setDepotForm({
                          ...depotForm, 
                          capacities: {...depotForm.capacities, [cls]: e.target.value}
                        })}
                        className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm"
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={() => setShowCreateDepotDialog(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                  Créer le dépôt
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Depots List */}
        <div className="lg:col-span-1">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Warehouse className="w-4 h-4" />
                Dépôts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="h-[500px]">
                {depots.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Warehouse className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun dépôt</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {depots.map((depot) => (
                      <div
                        key={depot.id}
                        onClick={() => setSelectedDepot(depot)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedDepot?.id === depot.id
                            ? 'bg-amber-500/10 border border-amber-500/30'
                            : 'bg-zinc-950/50 border border-zinc-800 hover:border-zinc-700'
                        }`}
                        data-testid={`depot-item-${depot.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-white">{depot.name}</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${
                            selectedDepot?.id === depot.id ? 'text-amber-400 rotate-90' : 'text-zinc-500'
                          }`} />
                        </div>
                        <p className="text-xs font-mono text-zinc-500 mb-2">{depot.code}</p>
                        {/* Mini capacity bars — classes ADR */}
                        <div className="grid grid-cols-5 gap-1">
                          {STORAGE_CLASSES.map(cls => {
                            const percent = getCapacityPercent(depot, cls);
                            return (
                              <div key={cls} className="space-y-0.5">
                                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${getCapacityColor(percent)}`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                                <p className="text-[9px] text-zinc-600 text-center">{cls}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {selectedDepot ? (
            <>
              {/* Depot Details */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-heading text-white">{selectedDepot.name}</CardTitle>
                      <p className="text-sm text-zinc-400 mt-1">{selectedDepot.address || 'Aucune adresse'}</p>
                    </div>
                    <Button variant="outline" size="sm" className="border-zinc-700" onClick={handleOpenEditDepot} data-testid="depot-settings-button">
                      <Settings className="w-4 h-4 mr-2" />
                      Paramètres
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {STORAGE_CLASSES.map(cls => {
                      const capacity = selectedDepot.capacities?.[cls] || 0;
                      const usage = selectedDepot.current_usage?.[cls]?.nec || 0;
                      const percent = capacity > 0 ? (usage / capacity) * 100 : 0;
                      const isOverThreshold = percent >= selectedDepot.alert_threshold_percent;

                      return (
                        <div 
                          key={cls} 
                          className={`p-4 rounded-lg border ${
                            isOverThreshold ? 'bg-amber-500/5 border-amber-500/30' : 'bg-zinc-950/50 border-zinc-800'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className={`${
                              cls === '1.1G' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                              cls === '1.2G' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                              cls === '1.3G' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                              cls === '1.4G' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                              'bg-blue-500/20 text-blue-400 border-blue-500/30'
                            }`}>{cls}</Badge>
                            {isOverThreshold && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                          </div>
                          <div className="space-y-2">
                            <Progress 
                              value={Math.min(percent, 100)} 
                              className={`h-2 ${percent >= 100 ? '[&>div]:bg-red-500' : percent >= 80 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'}`}
                            />
                            <div className="flex justify-between text-xs">
                              <span className="text-zinc-400">{usage.toFixed(2)} kg</span>
                              <span className="text-zinc-500">/ {capacity} kg</span>
                            </div>
                            <p className="text-lg font-bold text-white text-center">{percent.toFixed(1)}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Map */}
              {(selectedDepot.latitude && selectedDepot.longitude) && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Localisation geographique
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg overflow-hidden border border-zinc-800" style={{height: '280px'}}>
                      <iframe
                        title="Carte depot"
                        width="100%" height="100%" frameBorder="0" scrolling="no"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedDepot.longitude-0.01}%2C${selectedDepot.latitude-0.007}%2C${selectedDepot.longitude+0.01}%2C${selectedDepot.latitude+0.007}&layer=mapnik&marker=${selectedDepot.latitude}%2C${selectedDepot.longitude}`}
                      />
                    </div>
                    <p className="text-zinc-500 text-xs mt-2 font-mono">
                      {selectedDepot.latitude.toFixed(6)}, {selectedDepot.longitude.toFixed(6)}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Zones & Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Zones */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Zones
                      </CardTitle>
                      <Dialog open={showCreateZoneDialog} onOpenChange={setShowCreateZoneDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-zinc-700 h-7">
                            <Plus className="w-3 h-3 mr-1" /> Zone
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-800">
                          <DialogHeader>
                            <DialogTitle className="text-white">Nouvelle Zone</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleCreateZone} className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-zinc-300">Nom *</Label>
                              <Input
                                value={zoneForm.name}
                                onChange={(e) => setZoneForm({...zoneForm, name: e.target.value})}
                                className="bg-zinc-950 border-zinc-800 text-white"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-zinc-300">Code *</Label>
                              <Input
                                value={zoneForm.code}
                                onChange={(e) => setZoneForm({...zoneForm, code: e.target.value})}
                                className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-zinc-300">Type</Label>
                              <Select value={zoneForm.zone_type} onValueChange={(v) => setZoneForm({...zoneForm, zone_type: v})}>
                                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  <SelectItem value="storage" className="text-white">Stockage</SelectItem>
                                  <SelectItem value="reception" className="text-white">Réception</SelectItem>
                                  <SelectItem value="preparation" className="text-white">Préparation</SelectItem>
                                  <SelectItem value="quarantine" className="text-white">Quarantaine</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="outline" onClick={() => setShowCreateZoneDialog(false)} className="border-zinc-700">
                                Annuler
                              </Button>
                              <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                                Créer
                              </Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ScrollArea className="h-[300px]">
                      {zones.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                          <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Aucune zone</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {zones.map((zone) => (
                            <div
                              key={zone.id}
                              onClick={() => setSelectedZone(zone)}
                              className={`p-3 rounded-lg cursor-pointer transition-all ${
                                selectedZone?.id === zone.id
                                  ? 'bg-cyan-500/10 border border-cyan-500/30'
                                  : 'bg-zinc-950/50 border border-zinc-800 hover:border-zinc-700'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-white">{zone.name}</p>
                                  <p className="text-xs font-mono text-zinc-500">{zone.code}</p>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {zone.zone_type}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Locations */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Emplacements {selectedZone && `- ${selectedZone.name}`}
                      </CardTitle>
                      {selectedZone && (
                        <Dialog open={showCreateLocationDialog} onOpenChange={setShowCreateLocationDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-zinc-700 h-7">
                              <Plus className="w-3 h-3 mr-1" /> Emplacement
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-zinc-900 border-zinc-800">
                            <DialogHeader>
                              <DialogTitle className="text-white">Nouvel Emplacement</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateLocation} className="space-y-4">
                              <div className="grid grid-cols-4 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs text-zinc-400">Allée</Label>
                                  <Input
                                    value={locationForm.alley}
                                    onChange={(e) => setLocationForm({...locationForm, alley: e.target.value})}
                                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                    placeholder="A"
                                    required
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-zinc-400">Rack</Label>
                                  <Input
                                    value={locationForm.rack}
                                    onChange={(e) => setLocationForm({...locationForm, rack: e.target.value})}
                                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                    placeholder="01"
                                    required
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-zinc-400">Niveau</Label>
                                  <Input
                                    value={locationForm.level}
                                    onChange={(e) => setLocationForm({...locationForm, level: e.target.value})}
                                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                    placeholder="02"
                                    required
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-zinc-400">Position</Label>
                                  <Input
                                    value={locationForm.position}
                                    onChange={(e) => setLocationForm({...locationForm, position: e.target.value})}
                                    className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                    placeholder="03"
                                    required
                                  />
                                </div>
                              </div>
                              <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                                <p className="text-xs text-zinc-500 mb-1">Code généré</p>
                                <p className="font-mono text-lg text-cyan-400">
                                  {locationForm.alley || 'A'}-{locationForm.rack || '00'}-{locationForm.level || '00'}-{locationForm.position || '00'}
                                </p>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-zinc-300">NEC max (kg)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={locationForm.max_nec}
                                  onChange={(e) => setLocationForm({...locationForm, max_nec: e.target.value})}
                                  className="bg-zinc-950 border-zinc-800 text-white font-mono"
                                  placeholder="Optionnel"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setShowCreateLocationDialog(false)} className="border-zinc-700">
                                  Annuler
                                </Button>
                                <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                                  Créer
                                </Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ScrollArea className="h-[300px]">
                      {!selectedZone ? (
                        <div className="text-center py-8 text-zinc-500">
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Sélectionnez une zone</p>
                        </div>
                      ) : locations.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Aucun emplacement</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {locations.map((loc) => (
                            <div
                              key={loc.id}
                              className="p-3 rounded-lg bg-zinc-950/50 border border-zinc-800 hover:border-zinc-700 cursor-pointer"
                            >
                              <p className="font-mono text-sm text-cyan-400">{loc.code}</p>
                              {loc.max_nec && (
                                <p className="text-xs text-zinc-500 mt-1">Max: {loc.max_nec} kg</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-16 text-center">
                <Warehouse className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400">Sélectionnez un dépôt ou créez-en un nouveau</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Depot Dialog */}
      <Dialog open={showEditDepotDialog} onOpenChange={setShowEditDepotDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Paramètres du dépôt
            </DialogTitle>
          </DialogHeader>
          {editDepotForm && (
            <form onSubmit={handleSaveEditDepot} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Nom du dépôt *</Label>
                  <Input value={editDepotForm.name}
                    onChange={(e) => setEditDepotForm({...editDepotForm, name: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white" required data-testid="edit-depot-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Code *</Label>
                  <Input value={editDepotForm.code}
                    onChange={(e) => setEditDepotForm({...editDepotForm, code: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono" required data-testid="edit-depot-code" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Adresse</Label>
                <Input value={editDepotForm.address}
                  onChange={(e) => setEditDepotForm({...editDepotForm, address: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" placeholder="Adresse complete" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Latitude</Label>
                  <Input type="number" step="0.000001" value={editDepotForm.latitude || ''}
                    onChange={(e) => setEditDepotForm({...editDepotForm, latitude: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="43.0896" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Longitude</Label>
                  <Input type="number" step="0.000001" value={editDepotForm.longitude || ''}
                    onChange={(e) => setEditDepotForm({...editDepotForm, longitude: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="0.5917" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Seuil d'alerte capacité (%)</Label>
                <Input type="number" value={editDepotForm.alert_threshold_percent}
                  onChange={(e) => setEditDepotForm({...editDepotForm, alert_threshold_percent: parseInt(e.target.value)})}
                  className="bg-zinc-950 border-zinc-800 text-white w-32" min="50" max="100" />
              </div>
              <div className="space-y-3">
                <Label className="text-zinc-300">Capacités maximales par classe ADR (kg NEC)</Label>
                <p className="text-xs text-zinc-500">Timbrage réglementaire du dépôt</p>
                <div className="grid grid-cols-5 gap-3">
                  {STORAGE_CLASSES.map(cls => (
                    <div key={cls} className="space-y-1">
                      <Label className={`text-xs ${cls === '1.1G' ? 'text-red-400' : cls === '1.2G' ? 'text-orange-400' : cls === '1.3G' ? 'text-amber-400' : cls === '1.4G' ? 'text-green-400' : 'text-blue-400'}`}>{cls}</Label>
                      <Input type="number" step="0.1"
                        value={editDepotForm.capacities[cls] || ''}
                        onChange={(e) => setEditDepotForm({
                          ...editDepotForm,
                          capacities: {...editDepotForm.capacities, [cls]: e.target.value}
                        })}
                        className="bg-zinc-950 border-zinc-800 text-white font-mono text-sm" placeholder="0"
                        data-testid={`edit-depot-cap-${cls}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={handleDeleteDepot} data-testid="delete-depot-button">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer le dépôt
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditDepotDialog(false)} className="border-zinc-700">Annuler</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="save-edit-depot-button">
                    Enregistrer
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotsPage;
